import * as React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import CombinedBlurPaint from './combinedBlurPaint';
import AlphaRatio from './alphaRatio';
import {
  getPaintedPixelBounds,
  loadLinearLightImage,
  type LinearLightCaptureBounds,
} from './source';

export type PreparedCombinedBlur = {
  paint: string;
  footprint: string;
  backdrop?: string;
  bounds: LinearLightCaptureBounds;
  grid: number;
};

// Cache only the backdrop-independent SVG prefix. Native canvas blur has
// different large-radius sampling, so it cannot replace these SVG operations.
export async function prepareCombinedBlurSource(
  sourceUri: string,
  maskUri: string,
  width: number,
  blur: number,
  feather: number,
  paintMask?: string
): Promise<PreparedCombinedBlur> {
  const source = await loadLinearLightImage(sourceUri);
  const pixelWidth = source.naturalWidth;
  const pixelHeight = source.naturalHeight;
  const scale = pixelWidth / width;
  const height = pixelHeight / scale;
  const render = async (kind: 'paint' | 'footprint' | 'backdrop') => {
    const svg = renderToStaticMarkup(
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={pixelWidth}
        height={pixelHeight}
        viewBox={`0 0 ${width} ${height}`}
      >
        <defs>
          <filter
            id="prepare"
            filterUnits="userSpaceOnUse"
            x="0"
            y="0"
            width={width}
            height={height}
            colorInterpolationFilters="sRGB"
          >
            <feImage
              href={sourceUri}
              width={width}
              height={height}
              result="source"
            />
            <feImage
              href={maskUri}
              width={width}
              height={height}
              result="footprint"
            />
            {paintMask && (
              <feImage
                href={paintMask}
                width={width}
                height={height}
                result="paint-footprint"
              />
            )}
            <CombinedBlurPaint
              blur={blur}
              feather={feather}
              paintFrame={Boolean(paintMask)}
            />
            {kind === 'footprint' && (
              <feOffset
                in={
                  paintMask
                    ? feather > 0
                      ? 'soft-paint-footprint'
                      : 'paint-footprint'
                    : feather > 0
                    ? 'soft-footprint'
                    : 'footprint'
                }
                dx="0"
                dy="0"
              />
            )}
            {kind === 'backdrop' && (
              <AlphaRatio
                numerator={feather > 0 ? 'soft-footprint' : 'footprint'}
                denominator={
                  feather > 0 ? 'soft-paint-footprint' : 'paint-footprint'
                }
                result="backdrop-ratio"
              />
            )}
          </filter>
        </defs>
        <rect width={width} height={height} filter="url(#prepare)" />
      </svg>
    );
    const image = await loadLinearLightImage(
      `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
    );
    const canvas = document.createElement('canvas');
    canvas.width = pixelWidth;
    canvas.height = pixelHeight;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Blur capture requires a canvas context.');
    context.drawImage(image, 0, 0);
    return { canvas, context };
  };
  const paint = await render('paint');
  const footprint = await render('footprint');
  const backdrop = paintMask ? await render('backdrop') : undefined;
  const pixels = footprint.context.getImageData(0, 0, pixelWidth, pixelHeight);
  const coverage = getPaintedPixelBounds(pixels.data, pixelWidth, pixelHeight);
  // Preserve the original clipping at page edges. Four sigma plus two CSS
  // pixels retain backdrop samples beyond every nonzero footprint pixel.
  const padding = Math.ceil((4 * blur + 2) * scale);
  // Keep the live blur's downsample phase anchored to the original raster.
  const grid = 2 ** Math.ceil(Math.log2(Math.max(1, (blur * scale) / 4)));
  const x = Math.max(0, Math.floor((coverage.x - padding) / grid) * grid);
  const y = Math.max(0, Math.floor((coverage.y - padding) / grid) * grid);
  const croppedWidth =
    Math.min(
      pixelWidth,
      Math.ceil((coverage.x + coverage.width + padding) / grid) * grid
    ) - x;
  const croppedHeight =
    Math.min(
      pixelHeight,
      Math.ceil((coverage.y + coverage.height + padding) / grid) * grid
    ) - y;
  const crop = ({
    canvas,
    context,
  }: {
    canvas: HTMLCanvasElement;
    context: CanvasRenderingContext2D;
  }) => {
    const region = context.getImageData(x, y, croppedWidth, croppedHeight);
    canvas.width = croppedWidth;
    canvas.height = croppedHeight;
    context.putImageData(region, 0, 0);
    return canvas.toDataURL();
  };
  const prepared = {
    grid: grid / scale,
    paint: crop(paint),
    footprint: crop(footprint),
    backdrop: backdrop ? crop(backdrop) : undefined,
    bounds: {
      x: x / scale,
      y: y / scale,
      width: croppedWidth / scale,
      height: croppedHeight / scale,
    },
  };
  paint.canvas.width = 0;
  footprint.canvas.width = 0;
  await Promise.all([
    loadLinearLightImage(prepared.paint),
    loadLinearLightImage(prepared.footprint),
    ...(prepared.backdrop ? [loadLinearLightImage(prepared.backdrop)] : []),
  ]);
  return prepared;
}
